import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Tender,
  TenderCategory,
  TenderTypes,
  User,
  UserType,
} from '@app/models';
import { getTenders, getUsers, PageableParams } from '@app/api';
import { RenderFilterProps, SearchTemplate } from '@app/templates';
import UserCard from 'components/UserCard';
import { ModalProfile } from '@app/modals';
import { UsersFilter, UsersFilterData } from '@app/forms';
import { TenderCard } from '../index';
import fallbackImage from 'assets/images/vehicle_fallback.png';
import { useLocation } from 'react-router-dom';

interface Props {
  onClick?: (id: string) => void;
  refreshEventName?: string;
  defaultParams?: Partial<User | Tender>;
  initialFilter?: UsersFilterData<Tender | User>;
  isRenderFilter?: boolean;
  invertBgColorCard?: boolean;
  okedParentNumber?: number;
}

function UsersSearch(props: Props) {
  const {
    refreshEventName,
    defaultParams = {},
    onClick,
    initialFilter,
    isRenderFilter = true,
    invertBgColorCard = false,
    okedParentNumber,
  } = props;
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [tenderType, setTenderType] = useState<TenderTypes>(
    TenderTypes.REQUEST
  );
  const location = useLocation();

  const emptyTitle = useMemo(
    () =>
      tenderType === TenderTypes.REQUEST
        ? 'Вы еще не опубликовали заявки в категории «Специалисты»'
        : 'Список «Специалистов» пуст',
    [tenderType]
  );

  const onClickUser = useCallback(
    (clickedUser: User) => () => {
      setSelectedUser(clickedUser);
    },
    []
  );

  const renderItem = useCallback(
    (item: User | Tender, horizontal?: boolean) => {
      if (tenderType === TenderTypes.REQUEST) {
        return (
          <TenderCard
            horizontal={true}
            hideThumbnail={true}
            tender={item as Tender}
            onClick={onClick}
            fallbackImage={fallbackImage}
            tenderCategory={TenderCategory.WORKER}
          />
        );
      }

      return (
        <UserCard
          {...(item as User)}
          horizontal={true}
          onClick={onClickUser(item as User)}
          invertBgColorCard={invertBgColorCard}
        />
      );
    },
    [tenderType, onClickUser, invertBgColorCard, onClick]
  );

  const getData = useCallback(
    (params: PageableParams, filter?: UsersFilterData<Tender | User>) => {
      const { tenderType, ...restFilter } = filter!;

      setTenderType(tenderType);

      if (Number(tenderType) === TenderTypes.OFFER) {
        return getUsers({
          type: UserType.INDIVIDUAL,
          ...params,
          ...(defaultParams as Partial<User>),
          ...(restFilter as Partial<User>),
        });
      }

      return getTenders({
        ...params,
        ...(defaultParams as Partial<Tender>),
        ...(restFilter as Partial<Tender>),
        category: TenderCategory.WORKER,
      });
    },
    [defaultParams]
  );

  const onCloseModal = useCallback(() => {
    setSelectedUser(null);
  }, []);

  const renderFilter = useCallback(
    (filterProps: RenderFilterProps<UsersFilterData<Tender | User>>) => {
      if (isRenderFilter)
        return (
          <UsersFilter {...filterProps} okedParentNumber={okedParentNumber} />
        );
      return <></>;
    },
    [isRenderFilter, okedParentNumber]
  );

  return (
    <div>
      <SearchTemplate<Tender | User, UsersFilterData<Tender | User>>
        renderItem={renderItem}
        getData={getData}
        renderFilter={renderFilter}
        emptyTitle={emptyTitle}
        horizontal={true}
        refreshEventName={refreshEventName}
        viewControl={false}
        initialFilter={
          initialFilter || {
            tenderType: TenderTypes.REQUEST,
          }
        }
      />
      <ModalProfile
        visible={!!selectedUser}
        user={selectedUser}
        onClose={onCloseModal}
      />
    </div>
  );
}

export default memo(UsersSearch);
